/*
 * Copyright (C) 2021 DV Bern AG, Switzerland
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import {HttpErrorResponse} from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
    inject
} from '@angular/core';
import {FormBuilder} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {ReplaySubject, Subscription} from 'rxjs';
import {TSFerienbetreuungAngabenContainer} from '../../../../models/gemeindeantrag/TSFerienbetreuungAngabenContainer';
import {TSBenutzerNoDetails} from '../../../../models/TSBenutzerNoDetails';
import {ErrorService} from '../../../core/errors/service/ErrorService';
import {LogFactory} from '@utils/log';
import {BenutzerRSX} from '../../../core/service/benutzerRSX.rest';
import {FerienbetreuungService} from '../services/ferienbetreuung.service';

const LOG = LogFactory.createLog('FerienbetreuungSidePanelComponent');

@Component({
    selector: 'dv-ferienbetreuung-side-panel',
    templateUrl: './ferienbetreuung-side-panel.component.html',
    styleUrls: ['./ferienbetreuung-side-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FerienbetreuungSidePanelComponent implements OnInit, OnDestroy {
    private readonly ferienbetreuungService = inject(FerienbetreuungService);
    private readonly ref = inject(ChangeDetectorRef);
    private readonly errorService = inject(ErrorService);
    private readonly translate = inject(TranslateService);
    private readonly benutzerRS = inject(BenutzerRSX);
    private readonly fb = inject(FormBuilder);

    public form = this.fb.group({
        kommentar: this.fb.control({
            value: <null | string>null,
            disabled: true
        })
    });
    public saving$ = new ReplaySubject(1);
    private subscription: Subscription;
    public ferienbetreuungContainer: TSFerienbetreuungAngabenContainer;

    public userList: Array<TSBenutzerNoDetails>;

    public ngOnInit(): void {
        this.subscription = this.ferienbetreuungService
            .getFerienbetreuungContainer()
            .subscribe(
                container => {
                    this.ferienbetreuungContainer = container;
                    this.initForm();
                    this.ref.markForCheck();
                },
                err => LOG.error(err)
            );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public saveKommentar(): void {
        if (!this.form.valid) {
            return;
        }
        this.saving$.next(true);
        this.ferienbetreuungService
            .saveKommentar(
                this.ferienbetreuungContainer.id,
                this.form.getRawValue().kommentar
            )
            .subscribe(
                () => {
                    this.saving$.next(false);
                },
                (error: HttpErrorResponse) => {
                    LOG.error(error);
                    const translated = this.translate.instant(
                        'ERROR_LATS_KOMMENTAR_SAVE'
                    );
                    this.errorService.addMesageAsError(translated);
                }
            );
    }

    private initForm(): void {
        if (this.ferienbetreuungContainer?.isAbgeschlossen()) {
            this.form.controls.kommentar.disable();
        } else {
            this.form.controls.kommentar.enable();
        }
        this.form.controls.kommentar.setValue(
            this.ferienbetreuungContainer?.internerKommentar
        );
        this.loadUserList();
    }

    public getFerienbetreuungContainer(): TSFerienbetreuungAngabenContainer {
        return this.ferienbetreuungContainer;
    }

    public getVerantwortlicherFullName(): string {
        if (this.ferienbetreuungContainer.verantwortlicher) {
            return this.ferienbetreuungContainer.verantwortlicher.getFullName();
        }

        return this.translate.instant('NO_VERANTWORTLICHER_SELECTED');
    }

    public saveVerantwortlicher(): void {
        this.ferienbetreuungService.saveVerantwortlicher(
            this.ferienbetreuungContainer.id,
            this.ferienbetreuungContainer.verantwortlicher?.username
        );
    }

    private loadUserList(): void {
        this.benutzerRS.getAllActiveBenutzerMandant().then(response => {
            this.userList = response;
        });
    }
}
